import axios from 'axios';
import { clearAuthSessionStorage } from './authStorage';

const TOKEN_STORAGE_KEY = 'studyapp.com';
const NOTIFIED_KEY = 'sessionExpiredNotified';
const INTENDED_LOGIN_PATH_KEY = 'intendedLoginPath';
const SESSION_EXPIRED_ROUTE = '/session-expired';

export const MIN_SESSION_REMAINING_MS = 4 * 60 * 60 * 1000; // 4 hours. Backend JWT TTL is 30d (learner-app-server/authenticate.js), so this floor is comfortably safe for any fresh login.

const VALID_REASONS = ['expired', 'preflight'];

export function parseJwtExpMs(token) {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    if (!payload || !payload.exp) return null;
    const exp = payload.exp;
    return exp > 1e12 ? exp : exp * 1000;
  } catch (e) {
    return null;
  }
}

export function isValidSessionExpiredReason(reason) {
  return VALID_REASONS.includes(reason);
}

/**
 * Single place that resolves "when does the current session expire". Both the
 * pre-flight gate and the watcher go through this, so the ?sessionTestSec= QA
 * hook transparently applies to both instead of only the old countdown UI.
 */
// Caches the absolute forced-expiry timestamp for the current sessionTestSec
// value so repeated calls (e.g. once per second from SessionWatcher) see a
// fixed target instead of a moving "N seconds from right now" that never
// converges. Only recomputed when the query value itself changes.
let cachedForcedExpMs = null;
let cachedForcedRawValue = null;

export function getEffectiveExpMs() {
  try {
    const qs = new URLSearchParams(window.location.search);
    const testSecStr = qs.get('sessionTestSec');
    if (testSecStr) {
      const forcedSec = parseInt(testSecStr, 10);
      if (!isNaN(forcedSec) && forcedSec > 0) {
        if (cachedForcedRawValue !== testSecStr) {
          cachedForcedExpMs = Date.now() + forcedSec * 1000;
          cachedForcedRawValue = testSecStr;
        }
        // Clear the guard on every read while a forced override is active,
        // not just once, so repeated manual tests in the same browser
        // session aren't silently suppressed by a leftover idempotency flag
        // from a prior run.
        localStorage.removeItem(NOTIFIED_KEY);
        return cachedForcedExpMs;
      }
    } else {
      // Override removed from the URL — stop pinning to a stale forced value.
      cachedForcedExpMs = null;
      cachedForcedRawValue = null;
    }
  } catch (_) { /* ignore malformed query string */ }
  return parseJwtExpMs(localStorage.getItem(TOKEN_STORAGE_KEY));
}

/**
 * The single place that performs cleanup + redirect for any session-expiry
 * trigger (client-detected expiry, a server-rejected 401, or the pre-flight
 * gate blocking a test start). Idempotent: a second call while already
 * redirected/redirecting is a no-op.
 */
export function triggerSessionExpired(reason = 'expired') {
  if (window.location.pathname === SESSION_EXPIRED_ROUTE) return;
  if (localStorage.getItem(NOTIFIED_KEY) === '1') return;

  // Capture returnTo before any cleanup runs, and never let it point back at
  // the session-expired page itself.
  const currentPath = window.location.pathname + window.location.search;
  const returnTo = currentPath.startsWith(SESSION_EXPIRED_ROUTE) ? '/' : currentPath;

  const lastRole = localStorage.getItem('studyapp_role');
  const loginPath = (lastRole === 'student') ? '/student-login' : '/login';

  // Order matters: clearAuthSessionStorage() removes `sessionExpiredNotified`
  // (it's in LOCAL_STORAGE_SESSION_KEYS), so the guard must be set AFTER
  // cleanup or it gets silently wiped and repeated triggers double-redirect.
  clearAuthSessionStorage();
  delete axios.defaults.headers.common['Authorization'];
  localStorage.setItem(NOTIFIED_KEY, '1');
  localStorage.setItem(INTENDED_LOGIN_PATH_KEY, loginPath);

  const safeReason = isValidSessionExpiredReason(reason) ? reason : 'expired';
  window.location.assign(
    SESSION_EXPIRED_ROUTE +
    '?reason=' + encodeURIComponent(safeReason) +
    '&returnTo=' + encodeURIComponent(returnTo) +
    '&loginPath=' + encodeURIComponent(loginPath)
  );
}

/**
 * A student is only allowed to start a test/exam if the session has a
 * generous cushion of time left (MIN_SESSION_REMAINING_MS) — a flat floor
 * rather than a per-test-duration buffer, since it also has to cover
 * real-world slop (breaks, slow review, connectivity hiccups), not just the
 * nominal test length. Fails closed if the token can't be parsed at all.
 */
export function hasEnoughSessionTimeToStartTest() {
  const expMs = getEffectiveExpMs();
  if (expMs == null) return false;
  const remainingMs = expMs - Date.now();
  return remainingMs >= MIN_SESSION_REMAINING_MS;
}

/**
 * Shared gate every test/exam start path must go through. Supports async
 * start functions (several start handlers await a questions fetch before
 * flipping test-in-progress state).
 */
export async function ensureSessionTimeForTestStart(startFn) {
  if (!hasEnoughSessionTimeToStartTest()) {
    triggerSessionExpired('preflight');
    return false;
  }
  await startFn();
  return true;
}
